"use client"

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { supabase } from "@/utils/supabase/client";
import { Loader2 } from 'lucide-react';
import { format } from 'date-fns';
import { Payment, Doctor, Appointment, Patient } from '@/types';

interface FinancialAnalyticsProps {
    organizationId: string;
}

// A helper type for our specific query
type PaymentWithRelations = Payment & {
    appointments: Appointment & {
        doctors: Doctor;
        patients: Patient;
    }
};

export const FinancialAnalytics: React.FC<FinancialAnalyticsProps> = ({ organizationId }) => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [data, setData] = useState<{
        revenueByDoctor: { name: string; value: number }[];
        monthlyRevenue: { name: string; total: number }[];
    } | null>(null);

    useEffect(() => {
        const fetchData = async () => {
            if (!organizationId) return;

            try {
                const { data: payments, error } = await supabase
                    .from('payments')
                    .select(`
                        *,
                        appointments (
                            *,
                            doctors (*),
                            patients (*)
                        )
                    `)
                    .eq('organization_id', organizationId)
                    .in('status', ['Paid', 'Completed']);

                if (error) throw error;
                
                const typedPayments = payments as unknown as PaymentWithRelations[];

                const revenueByDoctor = typedPayments.reduce((acc, p) => {
                    const doctorName = p.appointments?.doctors?.name || 'Unknown Doctor';
                    acc[doctorName] = (acc[doctorName] || 0) + p.amount_paid;
                    return acc;
                }, {} as Record<string, number>);

                const monthlyRevenue = typedPayments.reduce((acc, p) => {
                    const month = format(new Date(p.created_at), 'MMM yyyy');
                    acc[month] = (acc[month] || 0) + p.amount_paid;
                    return acc;
                }, {} as Record<string, number>);

                setData({
                    revenueByDoctor: Object.entries(revenueByDoctor).map(([name, value]) => ({ name, value })),
                    monthlyRevenue: Object.entries(monthlyRevenue).map(([name, total]) => ({ name, total })),
                });

            } catch (err: any) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [organizationId]);

    if (loading) {
        return <div className="flex justify-center items-center h-96"><Loader2 className="w-8 h-8 animate-spin" /></div>
    }

    if (error) {
        return <div className="text-red-500 p-4">Error: {error}</div>
    }

    if (!data) {
        return <div>No data to display.</div>
    }

    return (
        <div className="space-y-6 mt-6">
            <div className="grid gap-6 md:grid-cols-2">
                <Card>
                    <CardHeader><CardTitle>Revenue by Doctor</CardTitle></CardHeader>
                    <CardContent>
                    <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={data.revenueByDoctor}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis type="number" />
                            <YAxis type="category" dataKey="name" width={80} />
                            <Tooltip />
                            <Legend />
                            <Bar dataKey="value" fill="#8884d8" name="Revenue" />
                        </BarChart>
                    </ResponsiveContainer>
                    </CardContent>
                </Card>
                <Card>
                    <CardHeader><CardTitle>Monthly Revenue Trend</CardTitle></CardHeader>
                    <CardContent>
                        <ResponsiveContainer width="100%" height={400}>
                            <BarChart data={data.monthlyRevenue}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="name" />
                                <YAxis />
                                <Tooltip />
                                <Legend />
                                <Bar dataKey="total" fill="#ffc658" name="Monthly Revenue"/>
                            </BarChart>
                        </ResponsiveContainer>
                    </CardContent>
                </Card>
            </div>
        </div>
    )
}